import React, { useEffect, useRef, useState } from "react";
import {
 Box,
 Flex,
 Input,
 InputGroup,
 InputLeftElement,
 Icon,
 Menu,
 MenuButton,
 MenuList,
 MenuItem,
 HStack,
 VStack,
 Accordion,
 AccordionItem,
 AccordionButton,
 AccordionPanel,
 AccordionIcon,
 IconButton,
 useDisclosure,
 Link as ChakraLink,
 Text,
 Image,
 Button,
} from "@chakra-ui/react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaWhatsapp, FaSearch } from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
import logo from "./logo (1) 4.png";

const NavLink = ({ to, children }) => {
 const location = useLocation();
 const isActive = location.pathname === to;

 return (
  <ChakraLink
   as={Link}
   to={to}
   fontWeight="500"
   fontSize="1rem"
   color={isActive ? "#01B4EC" : "#000"}
   borderBottom={isActive ? "2px solid #01B4EC" : "2px solid transparent"}
   _hover={{ color: "#01B4EC" }}
   transition="all 0.2s ease-in-out"
   px={2}
   py={1}
  >
   {children}
  </ChakraLink>
 );
};
const DropdownMenu = ({ label, items }) => {
 const isMedical = label === "Medical In India";

 return (
  <Menu>
   <MenuButton
    fontWeight="500"
    fontSize="1rem"
    color="#000"
    _hover={{ color: "#01B4EC" }}
   >
    {label} <ChevronDownIcon />
   </MenuButton>

   <MenuList
    zIndex={1000}
    p={isMedical ? 4 : 2}
    minW={isMedical ? "750px" : "200px"}
    maxW="90vw"
    overflow="hidden"
    borderRadius="md"
    boxShadow="lg"
    marginLeft={"90px"}
   >
    {isMedical ? (
     <Box
      display="grid"
      gridTemplateColumns={[
       "repeat(1, 1fr)",
       "repeat(2, 1fr)",
       "repeat(4, 1fr)",
      ]}
      gap={4}
     >
      {items.map((item, i) => (
       <MenuItem
        key={i}
        as={Link}
        to={item.to}
        _hover={{ color: "#01B4EC", bg: "transparent" }}
       >
        {item.label}
       </MenuItem>
      ))}
     </Box>
    ) : (
     items.map((item, i) => (
      <MenuItem key={i} as={Link} to={item.to} _hover={{ color: "#01B4EC" }}>
       {item.label}
      </MenuItem>
     ))
    )}
   </MenuList>
  </Menu>
 );
};

const Header = () => {
 const navigate = useNavigate();
 const { isOpen, onOpen, onClose } = useDisclosure();
 const [searchTerm, setSearchTerm] = useState("");
 const [suggestions, setSuggestions] = useState([]);
 const searchRef = useRef();
 const [isSearchOpen, setIsSearchOpen] = useState(false);
 const handleSearch = async (e) => {
  const value = e.target.value;
  setSearchTerm(value);

  if (value.length > 1) {
   try {
    const res = await fetch(
     `https://admission-backend-theta.vercel.app/api/universities/search?query=${value}`
    );
    const data = await res.json();
    setSuggestions(data.universities);
   } catch (error) {
    console.error("Error fetching suggestions:", error);
   }
  } else {
   setSuggestions([]);
  }
 };
 const toggleSearch = () => setIsSearchOpen((prev) => !prev);
 const handleSelect = (university) => {
  setSearchTerm(university.collegename);
  setSuggestions([]);
  navigate(`/universities/state/${university._id}`);
 };
 useEffect(() => {
  const handleClickOutside = (event) => {
   if (searchRef.current && !searchRef.current.contains(event.target)) {
    setSuggestions([]);
    setIsSearchOpen(false); // ✅ also close the mobile search bar
   }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
   document.removeEventListener("mousedown", handleClickOutside);
  };
 }, []);

 return (
  <Box
   position="sticky"
   top="0"
   zIndex="1000"
   bg="white"
   boxShadow="sm"
   fontFamily="Poppins"
  >
   {/* Top Row */}
   <Flex px={4} py={2.5} align="center" justify="space-between">
    {/* Logo + Brand */}
    <Flex
     align="center"
     justify="center"
     height="50px" // Match navbar height
     width="80px" // Give extra width for the scaled logo to not clip
     overflow="visible"
     cursor="pointer"
     onClick={() => navigate("/")}
    >
     <Image
      src={logo}
      alt="Logo"
      height={{ base: "40px", md: "50px" }}
      maxHeight="50px"
      objectFit="contain"
     />
    </Flex>

    {/* Search Bar */}

    <Box
     display={{ base: "none", md: "block" }}
     flex="1"
     maxW="400px"
     mx="auto"
     position="relative"
     ref={searchRef}
    >
     <InputGroup borderRadius="md" boxShadow="sm" size="sm">
      <InputLeftElement pointerEvents="none">
       <Icon as={FaSearch} color="gray.400" />
      </InputLeftElement>
      <Input
       placeholder="Search…"
       borderRadius="md"
       height="35px"
       value={searchTerm}
       onChange={handleSearch}
      />
     </InputGroup>

     {suggestions.length > 0 && (
      <Box
       position="absolute"
       mt={1}
       w="100%"
       bg="white"
       borderRadius="md"
       boxShadow="md"
       zIndex="1000"
       maxH="300px"
       overflowY="auto"
      >
       {suggestions.map((uni) => (
        <Box
         key={uni._id}
         p={2}
         borderBottom="1px solid #eee"
         _hover={{ bg: "gray.100" }}
         cursor="pointer"
         onClick={() => handleSelect(uni)}
        >
         {uni.collegename} – {uni.city}, {uni.state}
        </Box>
       ))}
      </Box>
     )}
    </Box>

    {/* 🔍 Search Icon for Mobile */}
    {isSearchOpen ? (
     <Box
      display={{ base: "block", md: "none" }}
      mt={2}
      px={2}
      position="relative"
      flex="1"
      ref={searchRef}
     >
      <InputGroup borderRadius="md" boxShadow="sm" size="sm">
       <InputLeftElement pointerEvents="none">
        <Icon as={FaSearch} color="gray.400" />
       </InputLeftElement>
       <Input
        placeholder="Search…"
        borderRadius="md"
        height="35px"
        value={searchTerm}
        onChange={handleSearch}
       />
      </InputGroup>

      {suggestions.length > 0 && (
       <Box
        position="absolute"
        mt={1}
        w="100%"
        bg="white"
        borderRadius="md"
        boxShadow="md"
        zIndex="1000"
        maxH="300px"
        overflowY="auto"
       >
        {suggestions.map((uni) => (
         <Box
          key={uni._id}
          p={2}
          borderBottom="1px solid #eee"
          _hover={{ bg: "gray.100" }}
          cursor="pointer"
          onClick={() => handleSelect(uni)}
         >
          {uni.collegename} – {uni.city}, {uni.state}
         </Box>
        ))}
       </Box>
      )}
     </Box>
    ) : (
     // Only show icon when search is closed
     <Box display={{ base: "block", md: "none" }}>
      <Button
       leftIcon={<FaSearch />}
       variant="solid"
       bg="#ffff"
       size="sm"
       onClick={toggleSearch}
      >
       Search
      </Button>
     </Box>
    )}
    {/* Contact Info – Hide on mobile if search is open */}
    {!isSearchOpen && (
     <HStack spacing={6} fontSize="sm" whiteSpace="nowrap">
      <HStack spacing={2}>
       <Icon as={FaWhatsapp} color="#01B4EC" />
       <Text>+91 9988661558</Text>
      </HStack>
      <HStack spacing={2} display={["none", "flex"]}>
       <Icon as={FiMail} color="#01B4EC" />
       <Text>Support@admissionpossibility.com</Text>
      </HStack>
     </HStack>
    )}
    {/* Hamburger menu: always visible on mobile */}
    <Box display={["flex", "flex", "none"]}>
     <Menu isOpen={isOpen}>
      <MenuButton
       as={IconButton}
       icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
       onClick={isOpen ? onClose : onOpen}
       aria-label="Toggle Menu"
      />
      <MenuList zIndex={9999} p={2}>
       <MenuItem as={Link} to="/" onClick={onClose}>
        Home
       </MenuItem>
       <MenuItem as={Link} to="/about" onClick={onClose}>
        About
       </MenuItem>
       <MenuItem as={Link} to="/gallery" onClick={onClose}>
        Gallery
       </MenuItem>
       <MenuItem as={Link} to="/deemed-university" onClick={onClose}>
        Deemed University
       </MenuItem>

       <Accordion allowToggle>
        <AccordionItem border="none">
         <AccordionButton px={0}>
          <Box flex="1" textAlign="left">
           Courses
          </Box>
          <AccordionIcon />
         </AccordionButton>
         <AccordionPanel p={0}>
          <VStack align="start" spacing={1} pl={4}>
           <ChakraLink as={Link} to="/courses/MBBS" onClick={onClose}>
            MBBS in India
           </ChakraLink>
           <ChakraLink as={Link} to="/courses/MD" onClick={onClose}>
            MD/MS in India
           </ChakraLink>
           <ChakraLink as={Link} to="/b-tech-list" onClick={onClose}>
            B.Tech in India
           </ChakraLink>
          </VStack>
         </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
         <AccordionButton px={0}>
          <Box flex="1" textAlign="left">
           Medical In India
          </Box>
          <AccordionIcon />
         </AccordionButton>
         <AccordionPanel p={0}>
          <VStack align="start" spacing={1} pl={4}>
           {[
            "Karnataka",
            "Assam",
            "Rajasthan",
            "Andhra Pradesh",
            "Jammu and Kashmir",
            "Dadar And Nagar Haveli",
            "Bihar",
            "Telangana",
            "Uttarakhand",
            "Kerala",
            "Sikkim",
            "Chhattisgarh",
            "Arunachal Pradesh",
            "Meghalaya",
            "Himachal Pradesh",
            "Gujarat",
            "West Bengal",
            "Maharashtra",
            "Delhi",
            "Tamil Nadu",
            "Orissa",
            "Goa",
            "Mizoram",
            "Punjab",
            "Jharkhand",
            "Uttar Pradesh",
           ].map((state, index) => (
            <ChakraLink
             key={index}
             as={Link}
             to={`/state/universities/${state.replace(/\s+/g, "")}`}
             onClick={onClose}
             _hover={{ color: "#01B4EC" }}
            >
             {state}
            </ChakraLink>
           ))}
          </VStack>
         </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
         <AccordionButton px={0}>
          <Box flex="1" textAlign="left">
           Abroad
          </Box>
          <AccordionIcon />
         </AccordionButton>
         <AccordionPanel p={0}>
          <VStack align="start" spacing={1} pl={4}>
           {[
            "Nepal",
            "Bangladesh",
            "Kyrgystan",
            "Russia",
            "Georgia",
            "Kazakhstan",
            "Uzbekistan",
            "Egypt",
            "Italy",
           ].map((country, index) => (
            <ChakraLink
             key={index}
             as={Link}
             to={`/state/${country}`}
             onClick={onClose}
             _hover={{ color: "#01B4EC" }}
            >
             MBBS in {country}
            </ChakraLink>
           ))}
          </VStack>
         </AccordionPanel>
        </AccordionItem>
       </Accordion>

       <MenuItem as={Link} to="/blogs" onClick={onClose}>
        Blog
       </MenuItem>
       <MenuItem as={Link} to="/contact" onClick={onClose}>
        Contact
       </MenuItem>
      </MenuList>
     </Menu>
    </Box>
   </Flex>

   {/* Desktop NavBar */}
   <Flex
    align="center"
    justify="center"
    w="100%"
    display={["none", "none", "flex"]}
   >
    <HStack
     spacing={6}
     maxW="1200px"
     w="100%"
     justify="center"
     align="center"
     flexWrap="wrap"
    >
     <NavLink to="/">Home</NavLink>
     <NavLink to="/about">About</NavLink>
     <NavLink to="/gallery">Gallery</NavLink>
     <NavLink to="/deemed-university">Deemed University</NavLink>
     <DropdownMenu
      label="Courses"
      items={[
       { label: "MBBS in India", to: "/courses/MBBS" },
       { label: "MD/MS in India", to: "/courses/MD" },
       { label: "B.Tech in India", to: "/b-tech-list" },
      ]}
     />
     <DropdownMenu
      label="Medical In India"
      items={[
       { label: "Karnataka", to: "/state/universities/Karnataka" },
       { label: "Assam", to: "/state/universities/Assam" },
       { label: "Rajasthan", to: "/state/universities/Rajasthan" },
       { label: "Andhra Pradesh", to: "/state/universities/AndhraPradesh" },
       { label: "Jammu and Kashmir", to: "/state/universities/JammuKashmir" },
       {
        label: "Dadar And Nagar Haveli",
        to: "/state/universities/DadarAndNagarHaveli",
       },
       { label: "Bihar", to: "/state/universities/Bihar" },
       { label: "Telangana", to: "/state/universities/Telangana" },
       { label: "Uttarakhand", to: "/state/universities/Uttarakhand" },
       { label: "Kerala", to: "/state/universities/Kerala" },
       { label: "Sikkim", to: "/state/universities/Sikkim" },
       { label: "Chhattisgarh", to: "/state/universities/Chhattisgarh" },
       {
        label: "Arunachal Pradesh",
        to: "/state/universities/ArunachalPradesh",
       },
       { label: "Meghalaya", to: "/state/universities/Meghalaya" },
       { label: "Himachal Pradesh", to: "/state/universities/HimachalPradesh" },
       { label: "Gujarat", to: "/state/universities/Gujarat" },
       { label: "West Bengal", to: "/state/universities/WestBengal" },
       { label: "Maharashtra", to: "/state/universities/Maharashtra" },
       { label: "Delhi", to: "/state/universities/Delhi" },
       { label: "Tamil Nadu", to: "/state/universities/TamilNadu" },
       { label: "Orissa", to: "/state/universities/Orissa" },
       { label: "Goa", to: "/state/universities/Goa" },
       { label: "Mizoram", to: "/state/universities/Mizoram" },
       { label: "Punjab", to: "/state/universities/Punjab" },
       { label: "Jharkhand", to: "/state/universities/Jharkhand" },
       { label: "Uttar Pradesh", to: "/state/universities/UttarPradesh" },
      ]}
     />

     <DropdownMenu
      label="Abroad"
      items={[
       { label: "MBBS in Nepal", to: "/state/Nepal" },
       { label: "MBBS in Bangladesh", to: "/state/Bangladesh" },
       { label: "MBBS in Kyrgystan", to: "/state/Kyrgystan" },
       { label: "MBBS in Russia", to: "/state/Russia" },
       { label: "MBBS in Georgia", to: "/state/Georgia" },
       { label: "MBBS in Kazakhstan", to: "/state/Kazakhstan" },
       { label: "MBBS in Uzbekistan", to: "/state/Uzbekistan" },
       { label: "MBBS in Egypt", to: "/state/Egypt" },
       { label: "MBBS in Italy", to: "/state/Italy" },
      ]}
     />

     <NavLink to="/blogs">Blog</NavLink>
     <NavLink to="/contact">Contact</NavLink>
    </HStack>
   </Flex>
  </Box>
 );
};

export default Header;

// import React, { useRef, useState } from "react";
// import {
//  Box,
//  Flex,
//  Input,
//  InputGroup,
//  InputLeftElement,
//  Icon,
//  Menu,
//  MenuButton,
//  MenuList,
//  MenuItem,
//  HStack,
//  VStack,
//  Accordion,
//  AccordionItem,
//  AccordionButton,
//  AccordionPanel,
//  AccordionIcon,
//  IconButton,
//  useDisclosure,
//  Link as ChakraLink,
//  Text,
//  Image,
// } from "@chakra-ui/react";
// import { Link, useNavigate, useLocation } from "react-router-dom";
// import { FaWhatsapp, FaSearch } from "react-icons/fa";
// import { FiMail } from "react-icons/fi";
// import { HamburgerIcon, CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
// import logo from "./logo (1) 4.png";

// const NavLink = ({ to, children }) => {
//  const location = useLocation();
//  const isActive = location.pathname === to;

//  return (
//   <ChakraLink
//    as={Link}
//    to={to}
//    fontWeight="500"
//    fontSize="1rem"
//    color={isActive ? "#01B4EC" : "#000"}
//    borderBottom={isActive ? "2px solid #01B4EC" : "2px solid transparent"}
//    _hover={{ color: "#01B4EC" }}
//    transition="all 0.2s ease-in-out"
//    px={2}
//    py={1}
//   >
//    {children}
//   </ChakraLink>
//  );
// };
// const DropdownMenu = ({ label, items }) => {
//  const isMedical = label === "Medical In India";

//  return (
//   <Menu>
//    <MenuButton
//     fontWeight="500"
//     fontSize="1rem"
//     color="#000"
//     _hover={{ color: "#01B4EC" }}
//    >
//     {label} <ChevronDownIcon />
//    </MenuButton>

//    <MenuList
//     zIndex={1000}
//     p={isMedical ? 4 : 2}
//     minW={isMedical ? "750px" : "200px"}
//     maxW="90vw"
//     overflow="hidden"
//     borderRadius="md"
//     boxShadow="lg"
//     marginLeft={"90px"}
//    >
//     {isMedical ? (
//      <Box
//       display="grid"
//       gridTemplateColumns={[
//        "repeat(1, 1fr)",
//        "repeat(2, 1fr)",
//        "repeat(4, 1fr)",
//       ]}
//       gap={4}
//      >
//       {items.map((item, i) => (
//        <MenuItem
//         key={i}
//         as={Link}
//         to={item.to}
//         _hover={{ color: "#01B4EC", bg: "transparent" }}
//        >
//         {item.label}
//        </MenuItem>
//       ))}
//      </Box>
//     ) : (
//      items.map((item, i) => (
//       <MenuItem key={i} as={Link} to={item.to} _hover={{ color: "#01B4EC" }}>
//        {item.label}
//       </MenuItem>
//      ))
//     )}
//    </MenuList>
//   </Menu>
//  );
// };

// const Header = () => {
//  const navigate = useNavigate();
//  const { isOpen, onOpen, onClose } = useDisclosure();
//  const [searchTerm, setSearchTerm] = useState("");
//  const [suggestions, setSuggestions] = useState([]);
//  const searchRef = useRef();

//  const handleSearch = async (e) => {
//   const value = e.target.value;
//   setSearchTerm(value);

//   if (value.length > 1) {
//    try {
//     const res = await fetch(
//      `https://admission-backend-theta.vercel.app/api/universities/search?query=${value}`
//     );
//     const data = await res.json();
//     setSuggestions(data.universities);
//    } catch (error) {
//     console.error("Error fetching suggestions:", error);
//    }
//   } else {
//    setSuggestions([]);
//   }
//  };

//  const handleSelect = (university) => {
//   setSearchTerm(university.collegename);
//   setSuggestions([]);
//   navigate(`/universities/state/${university._id}`);
//  };

//  return (
//   <Box
//    position="sticky"
//    top="0"
//    zIndex="1000"
//    bg="white"
//    boxShadow="sm"
//    fontFamily="Poppins"
//   >
//    {/* Top Row */}
//    <Flex px={4} py={2.5} align="center" justify="space-between">
//     {/* Logo + Brand */}
//     <Flex
//      align="center"
//      justify="center"
//      height="50px" // Match navbar height
//      width="80px" // Give extra width for the scaled logo to not clip
//      overflow="visible"
//      cursor="pointer"
//      onClick={() => navigate("/")}
//     >
//      <Image
//       src={logo}
//       alt="Logo"
//       height={{ base: "40px", md: "50px" }}
//       maxHeight="50px"
//       objectFit="contain"
//      />
//     </Flex>

//     {/* Search Bar */}
//     <Box display={["none", "none", "block"]} flex="1" maxW="400px" mx="auto">
//      <InputGroup borderRadius="md" boxShadow="sm" size="sm">
//       <InputLeftElement pointerEvents="none">
//        <Icon as={FaSearch} color="gray.400" />
//       </InputLeftElement>
//       <Input
//        placeholder="Search…"
//        borderRadius="md"
//        height="35px"
//        value={searchTerm}
//        onChange={handleSearch}
//       />
//      </InputGroup>
//      {suggestions.length > 0 && (
//       <Box
//        ref={searchRef}
//        position="absolute"
//        mt={1}
//        w="100%"
//        bg="white"
//        borderRadius="md"
//        boxShadow="md"
//        zIndex="1000"
//        maxH="300px"
//        overflowY="auto"
//       >
//        {suggestions.map((uni) => (
//         <Box
//          key={uni._id}
//          p={2}
//          borderBottom="1px solid #eee"
//          _hover={{ bg: "gray.100" }}
//          cursor="pointer"
//          onClick={() => handleSelect(uni)}
//         >
//          {uni.collegename} – {uni.city}, {uni.state}
//         </Box>
//        ))}
//       </Box>
//      )}
//     </Box>

//     {/* Contact Info */}
//     <HStack spacing={6} fontSize="sm" whiteSpace="nowrap">
//      <HStack spacing={2}>
//       <Icon as={FaWhatsapp} color="#01B4EC" />
//       <Text>+91 9988661558</Text>
//      </HStack>
//      <HStack spacing={2} display={["none", "flex"]}>
//       <Icon as={FiMail} color="#01B4EC" />
//       <Text>Support@admissionpossibility.com</Text>
//      </HStack>

//      {/* Hamburger Menu */}
//      <Box display={["flex", "flex", "none"]}>
//       <Menu isOpen={isOpen}>
//        <MenuButton
//         as={IconButton}
//         icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
//         onClick={isOpen ? onClose : onOpen}
//         aria-label="Toggle Menu"
//        />
//        <MenuList zIndex={9999} p={2}>
//         <MenuItem as={Link} to="/" onClick={onClose}>
//          Home
//         </MenuItem>
//         <MenuItem as={Link} to="/about" onClick={onClose}>
//          About
//         </MenuItem>
//         <MenuItem as={Link} to="/gallery" onClick={onClose}>
//          Gallery
//         </MenuItem>
//         <MenuItem as={Link} to="/deemed-university" onClick={onClose}>
//          Deemed University
//         </MenuItem>

//         <Accordion allowToggle>
//          <AccordionItem border="none">
//           <AccordionButton px={0}>
//            <Box flex="1" textAlign="left">
//             Courses
//            </Box>
//            <AccordionIcon />
//           </AccordionButton>
//           <AccordionPanel p={0}>
//            <VStack align="start" spacing={1} pl={4}>
//             <ChakraLink as={Link} to="/courses/MBBS" onClick={onClose}>
//              MBBS in India
//             </ChakraLink>
//             <ChakraLink as={Link} to="/courses/MD" onClick={onClose}>
//              MD/MS in India
//             </ChakraLink>
//             <ChakraLink as={Link} to="/b-tech-list" onClick={onClose}>
//              B.Tech in India
//             </ChakraLink>
//            </VStack>
//           </AccordionPanel>
//          </AccordionItem>

//          <AccordionItem border="none">
//           <AccordionButton px={0}>
//            <Box flex="1" textAlign="left">
//             Medical In India
//            </Box>
//            <AccordionIcon />
//           </AccordionButton>
//           <AccordionPanel p={0}>
//            <VStack align="start" spacing={1} pl={4}>
//             {[
//              { label: "Karnataka", to: "/state/universities/Karnataka" },
//              { label: "Assam", to: "/state/universities/Assam" },
//              { label: "Rajasthan", to: "/state/universities/Rajasthan" },
//              {
//               label: "Andhra Pradesh",
//               to: "/state/universities/AndhraPradesh",
//              },
//              {
//               label: "Jammu and Kashmir",
//               to: "/state/universities/JammuKashmir",
//              },
//              {
//               label: "Dadar And Nagar Haveli",
//               to: "/state/universities/DadarAndNagarHaveli",
//              },
//              { label: "Bihar", to: "/state/universities/Bihar" },
//              { label: "Telangana", to: "/state/universities/Telangana" },
//              { label: "Uttarakhand", to: "/state/universities/Uttarakhand" },
//              { label: "Kerala", to: "/state/universities/Kerala" },
//              { label: "Sikkim", to: "/state/universities/Sikkim" },
//              { label: "Chhattisgarh", to: "/state/universities/Chhattisgarh" },
//              {
//               label: "Arunachal Pradesh",
//               to: "/state/universities/ArunachalPradesh",
//              },
//              { label: "Meghalaya", to: "/state/universities/Meghalaya" },
//              {
//               label: "Himachal Pradesh",
//               to: "/state/universities/HimachalPradesh",
//              },
//              { label: "Gujarat", to: "/state/universities/Gujarat" },
//              { label: "West Bengal", to: "/state/universities/WestBengal" },
//              { label: "Maharashtra", to: "/state/universities/Maharashtra" },
//              { label: "Delhi", to: "/state/universities/Delhi" },
//              { label: "Tamil Nadu", to: "/state/universities/TamilNadu" },
//              { label: "Orissa", to: "/state/universities/Orissa" },
//              { label: "Goa", to: "/state/universities/Goa" },
//              { label: "Mizoram", to: "/state/universities/Mizoram" },
//              { label: "Punjab", to: "/state/universities/Punjab" },
//              { label: "Jharkhand", to: "/state/universities/Jharkhand" },
//              { label: "Uttar Pradesh", to: "/state/universities/UttarPradesh" },
//             ].map((state, index) => (
//              <ChakraLink
//               key={index}
//               as={Link}
//               to={state.to}
//               onClick={onClose}
//               _hover={{ color: "#01B4EC" }}
//              >
//               {state.label}
//              </ChakraLink>
//             ))}
//            </VStack>
//           </AccordionPanel>
//          </AccordionItem>

//          <AccordionItem border="none">
//           <AccordionButton px={0}>
//            <Box flex="1" textAlign="left">
//             Abroad
//            </Box>
//            <AccordionIcon />
//           </AccordionButton>
//           <AccordionPanel p={0}>
//            <VStack align="start" spacing={1} pl={4}>
//             {[
//              { label: "MBBS in Nepal", to: "/state/Nepal" },
//              { label: "MBBS in Bangladesh", to: "/state/Bangladesh" },
//              { label: "MBBS in Kyrgystan", to: "/state/Kyrgystan" },
//              { label: "MBBS in Russia", to: "/state/Russia" },
//              { label: "MBBS in Georgia", to: "/state/Georgia" },
//              { label: "MBBS in Kazakhstan", to: "/state/Kazakhstan" },
//              { label: "MBBS in Uzbekistan", to: "/state/Uzbekistan" },
//              { label: "MBBS in Egypt", to: "/state/Egypt" },
//              { label: "MBBS in Italy", to: "/state/Italy" },
//             ].map((item, index) => (
//              <ChakraLink
//               key={index}
//               as={Link}
//               to={item.to}
//               onClick={onClose}
//               _hover={{ color: "#01B4EC" }}
//              >
//               {item.label}
//              </ChakraLink>
//             ))}
//            </VStack>
//           </AccordionPanel>
//          </AccordionItem>
//         </Accordion>

//         <MenuItem as={Link} to="/blogs" onClick={onClose}>
//          Blog
//         </MenuItem>
//         <MenuItem as={Link} to="/contact" onClick={onClose}>
//          Contact
//         </MenuItem>
//        </MenuList>
//       </Menu>
//      </Box>
//     </HStack>
//    </Flex>

//    {/* Desktop NavBar */}
//    <Flex
//     align="center"
//     justify="center"
//     w="100%"
//     display={["none", "none", "flex"]}
//    >
//     <HStack
//      spacing={6}
//      maxW="1200px"
//      w="100%"
//      justify="center"
//      align="center"
//      flexWrap="wrap"
//     >
//      <NavLink to="/">Home</NavLink>
//      <NavLink to="/about">About</NavLink>
//      <NavLink to="/gallery">Gallery</NavLink>
//      <NavLink to="/deemed-university">Deemed University</NavLink>
//      <DropdownMenu
//       label="Courses"
//       items={[
//        { label: "MBBS in India", to: "/courses/MBBS" },
//        { label: "MD/MS in India", to: "/courses/MD" },
//        { label: "B.Tech in India", to: "/b-tech-list" },
//       ]}
//      />
//      <DropdownMenu
//       label="Medical In India"
//       items={[
//        { label: "Karnataka", to: "/state/universities/Karnataka" },
//        { label: "Assam", to: "/state/universities/Assam" },
//        { label: "Rajasthan", to: "/state/universities/Rajasthan" },
//        { label: "Andhra Pradesh", to: "/state/universities/AndhraPradesh" },
//        { label: "Jammu and Kashmir", to: "/state/universities/JammuKashmir" },
//        {
//         label: "Dadar And Nagar Haveli",
//         to: "/state/universities/DadarAndNagarHaveli",
//        },
//        { label: "Bihar", to: "/state/universities/Bihar" },
//        { label: "Telangana", to: "/state/universities/Telangana" },
//        { label: "Uttarakhand", to: "/state/universities/Uttarakhand" },
//        { label: "Kerala", to: "/state/universities/Kerala" },
//        { label: "Sikkim", to: "/state/universities/Sikkim" },
//        { label: "Chhattisgarh", to: "/state/universities/Chhattisgarh" },
//        {
//         label: "Arunachal Pradesh",
//         to: "/state/universities/ArunachalPradesh",
//        },
//        { label: "Meghalaya", to: "/state/universities/Meghalaya" },
//        { label: "Himachal Pradesh", to: "/state/universities/HimachalPradesh" },
//        { label: "Gujarat", to: "/state/universities/Gujarat" },
//        { label: "West Bengal", to: "/state/universities/WestBengal" },
//        { label: "Maharashtra", to: "/state/universities/Maharashtra" },
//        { label: "Delhi", to: "/state/universities/Delhi" },
//        { label: "Tamil Nadu", to: "/state/universities/TamilNadu" },
//        { label: "Orissa", to: "/state/universities/Orissa" },
//        { label: "Goa", to: "/state/universities/Goa" },
//        { label: "Mizoram", to: "/state/universities/Mizoram" },
//        { label: "Punjab", to: "/state/universities/Punjab" },
//        { label: "Jharkhand", to: "/state/universities/Jharkhand" },
//        { label: "Uttar Pradesh", to: "/state/universities/UttarPradesh" },
//       ]}
//      />

//      <DropdownMenu
//       label="Abroad"
//       items={[
//        { label: "MBBS in Nepal", to: "/state/Nepal" },
//        { label: "MBBS in Bangladesh", to: "/state/Bangladesh" },
//        { label: "MBBS in Kyrgystan", to: "/state/Kyrgystan" },
//        { label: "MBBS in Russia", to: "/state/Russia" },
//        { label: "MBBS in Georgia", to: "/state/Georgia" },
//        { label: "MBBS in Kazakhstan", to: "/state/Kazakhstan" },
//        { label: "MBBS in Uzbekistan", to: "/state/Uzbekistan" },
//        { label: "MBBS in Egypt", to: "/state/Egypt" },
//        { label: "MBBS in Italy", to: "/state/Italy" },
//       ]}
//      />

//      <NavLink to="/blogs">Blog</NavLink>
//      <NavLink to="/contact">Contact</NavLink>
//     </HStack>
//    </Flex>
//   </Box>
//  );
// };

// export default Header;
